import { useState } from 'react';
import { t } from '@aquabox/shared';
import { ApiError } from '../api/client';
import { useCustomerMutations, useCustomers } from '../api/queries';
import type { Customer } from '../api/types';
import { useToast } from './Toast';

/**
 * Müşteri seçici: ad/telefonla arama + satır içi "Yeni müşteri". Yeni müşteri MEVCUT
 * `POST /api/customers` ucuyla açılır (Customers.tsx formuyla aynı gövde: ad, telefon, adres
 * zorunlu; biçim/normalizasyon kararı sunucuda) ve kaydedilince otomatik seçilir.
 * Sipariş girişi ve gün tahtasındaki "Müşteri bağla" aynı bileşeni kullanır.
 */
export function CustomerPicker({ onSelect }: { onSelect: (customer: Customer) => void }) {
  const { create } = useCustomerMutations();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [error, setError] = useState<string | null>(null);

  const term = q.trim();
  const results = useCustomers({ q: term.length >= 2 ? term : undefined, pageSize: '8' });
  const items = term.length >= 2 ? (results.data?.items ?? []) : [];

  function saveNew(): void {
    setError(null);
    if (!name.trim() || !phone.trim() || !address.trim()) {
      setError(t('newOrder.customer.fieldsRequired'));
      return;
    }
    create.mutate(
      { fullName: name.trim(), phone: phone.trim(), address: address.trim() },
      {
        onSuccess: (saved: Customer) => {
          toast(t('cust.created', { name: saved.fullName }), 'success');
          onSelect(saved);
        },
        // 409 (mükerrer telefon): sunucu mesajı başka müşterinin adını taşıyabilir, basılmaz.
        onError: (err: unknown) =>
          setError(
            err instanceof ApiError
              ? err.status === 409
                ? t('newOrder.customer.phoneTaken')
                : err.message
              : t('error.internal'),
          ),
      },
    );
  }

  if (creating) {
    return (
      <div data-testid="customer-picker-new" style={{ display: 'grid', gap: 10 }}>
        <input
          className="input"
          data-testid="picker-new-name"
          aria-label={t('newOrder.customer.newName')}
          placeholder={t('newOrder.customer.newName')}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          className="input"
          data-testid="picker-new-phone"
          inputMode="tel"
          aria-label={t('newOrder.customer.newPhone')}
          placeholder={t('newOrder.phone.ph')}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <input
          className="input"
          data-testid="picker-new-address"
          aria-label={t('newOrder.customer.newAddress')}
          placeholder={t('newOrder.address.ph')}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
        />
        {error && (
          <div role="alert" style={{ color: 'var(--danger)', fontSize: 13.5, fontWeight: 500 }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={create.isPending}
            onClick={saveNew}
          >
            {t('newOrder.customer.create')}
          </button>
          <button
            type="button"
            className="btn"
            style={{ color: 'var(--text-secondary)' }}
            onClick={() => setCreating(false)}
          >
            {t('newOrder.customer.cancelNew')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <input
        className="input"
        data-testid="picker-search"
        aria-label={t('newOrder.customer.label')}
        placeholder={t('newOrder.customer.search.ph')}
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {term.length >= 2 && results.isSuccess && items.length === 0 && (
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
          {t('newOrder.customer.none')}
        </p>
      )}
      {items.map((c) => (
        <button
          key={c.id}
          type="button"
          className="btn btn-secondary"
          data-testid="picker-result"
          style={{ justifyContent: 'space-between' }}
          onClick={() => onSelect(c)}
        >
          <span>{c.fullName}</span>
          <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{c.phone ?? ''}</span>
        </button>
      ))}
      <button
        type="button"
        className="btn"
        data-testid="picker-new"
        style={{ color: 'var(--primary)', fontSize: 13, padding: 0, justifySelf: 'start' }}
        onClick={() => setCreating(true)}
      >
        {t('newOrder.customer.new')}
      </button>
    </div>
  );
}
